import { useCallback, useEffect, useMemo, useState } from "react";
import { Copy, Loader2, Palette, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import {
  TOKEN_GROUPS, hslToCss, hslToHex, hexToHsl, applyEmpresaTheme, clearEmpresaTheme,
} from "@/lib/themeTokens";
import { sectionGradientStyle } from "@/lib/sectionTheme";
import { ctaButtonStyle, orgHeaderStyle } from "@/lib/uiTheme";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

function TokenField({ tokenKey, label, value, onChange }) {
  const hex = hslToHex(value);
  return (
    <div className="flex items-center gap-2 rounded-lg border border-zinc-200 bg-white p-2 dark:border-zinc-700 dark:bg-zinc-900">
      <input
        type="color"
        value={hex}
        onChange={(e) => onChange(tokenKey, hexToHsl(e.target.value))}
        className="h-9 w-9 shrink-0 cursor-pointer rounded border-0 bg-transparent p-0"
        aria-label={label}
      />
      <div className="min-w-0 flex-1">
        <p className="truncate text-xs font-medium text-zinc-800 dark:text-zinc-200">{label}</p>
        <Input
          value={value || ""}
          onChange={(e) => onChange(tokenKey, e.target.value)}
          className="mt-0.5 h-7 font-mono text-[11px]"
          placeholder="H S% L%"
        />
      </div>
      <div className="h-9 w-9 shrink-0 rounded-md border border-zinc-200" style={{ background: hslToCss(value) }} />
    </div>
  );
}

function TokenGroup({ group, values, onChange }) {
  const content = (
    <div className="mt-2 grid gap-2 sm:grid-cols-2">
      {group.tokens.map((t) => (
        <TokenField
          key={t.key}
          tokenKey={t.key}
          label={t.label}
          value={values?.[t.key] || ""}
          onChange={onChange}
        />
      ))}
    </div>
  );

  if (group.collapsible) {
    return (
      <details key={group.id} className="rounded-lg border border-zinc-200 bg-zinc-50/50 p-3 dark:border-zinc-700 dark:bg-zinc-900/40">
        <summary className="cursor-pointer text-xs font-semibold uppercase tracking-wide text-zinc-500">
          {group.label} ({group.tokens.length} colores)
        </summary>
        {content}
      </details>
    );
  }

  return (
    <div key={group.id}>
      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-zinc-500">{group.label}</p>
      {content}
    </div>
  );
}

function PreviewPanel({ colores, modo }) {
  const vars = colores?.[modo] || {};
  const style = {
    background: hslToCss(vars.background),
    color: hslToCss(vars.foreground),
    borderColor: hslToCss(vars.border),
  };
  return (
    <div className="rounded-xl border p-4" style={style}>
      <div
        className="mb-3 rounded-lg px-4 py-3 text-white"
        style={{
          background: `linear-gradient(to right, ${hslToCss(vars["header-from"])}, ${hslToCss(vars["header-via"])}, ${hslToCss(vars["header-to"])})`,
        }}
      >
        <p className="text-sm font-semibold">Vista previa cabecera</p>
      </div>
      <div
        className="mb-3 flex items-center gap-2 rounded-lg border px-3 py-2"
        style={{
          background: hslToCss(vars["brand-muted"]),
          borderColor: hslToCss(vars["brand-border"]),
          color: hslToCss(vars["brand-muted-foreground"]),
        }}
      >
        <div className="h-8 w-8 rounded-lg" style={{ background: hslToCss(vars.brand) }} />
        <span className="text-sm font-medium">Marca corporativa</span>
      </div>
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          className="rounded-lg px-3 py-1.5 text-sm font-medium"
          style={{ background: hslToCss(vars.primary), color: hslToCss(vars["primary-foreground"]) }}
        >
          Primario
        </button>
        <button
          type="button"
          className="rounded-lg px-3 py-1.5 text-sm font-medium"
          style={{ background: hslToCss(vars.brand), color: hslToCss(vars["brand-foreground"]) }}
        >
          Marca
        </button>
        <button type="button" className="rounded-lg px-3 py-1.5 text-sm font-medium" style={ctaButtonStyle()}>
          Publicar (CTA)
        </button>
      </div>
      <div className="mt-3 grid gap-2 sm:grid-cols-2">
        <div className="rounded-lg px-3 py-2 text-sm text-white" style={orgHeaderStyle()}>
          Encabezado Organización
        </div>
        <div className="rounded-lg px-3 py-2 text-sm text-white" style={sectionGradientStyle("zonas")}>
          Zonas de trabajo
        </div>
        <div className="rounded-lg px-3 py-2 text-sm text-white" style={sectionGradientStyle("informes")}>
          Listados e informes
        </div>
        <div className="rounded-lg px-3 py-2 text-sm text-white" style={sectionGradientStyle("fichajes")}>
          Fichajes
        </div>
        <div className="rounded-lg px-3 py-2 text-sm text-white" style={sectionGradientStyle("gastos")}>
          Gastos
        </div>
        <div className="rounded-lg px-3 py-2 text-sm text-white" style={sectionGradientStyle("rutas")}>
          Rutas comerciales
        </div>
      </div>
    </div>
  );
}

export default function PlantillasColores() {
  const [items, setItems] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState({ nombre: "", colores: { light: {}, dark: {} } });
  const [modoPreview, setModoPreview] = useState("light");
  const [guardando, setGuardando] = useState(false);
  const [dupFor, setDupFor] = useState(null);
  const [dupNombre, setDupNombre] = useState("");

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const { data } = await api.get("/plantillas-colores");
      setItems(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  useEffect(() => {
    if (!edit) {
      clearEmpresaTheme(form.colores);
      return undefined;
    }
    applyEmpresaTheme(form.colores, modoPreview);
    return () => clearEmpresaTheme(form.colores);
  }, [edit, form.colores, modoPreview]);

  const abrirEditar = async (p) => {
    try {
      const { data } = await api.get(`/plantillas-colores/${p.id}`);
      setForm({ nombre: data.nombre, colores: data.colores });
      setEdit(data);
      setModoPreview("light");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const abrirNueva = async () => {
    try {
      const base = items.find((i) => i.es_default);
      if (base) {
        const det = await api.get(`/plantillas-colores/${base.id}`);
        setForm({ nombre: "", colores: structuredClone(det.data.colores) });
      } else {
        setForm({ nombre: "", colores: { light: {}, dark: {} } });
      }
      setEdit({ id: null, es_default: false });
      setModoPreview("light");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const onTokenChange = (modo, key, val) => {
    setForm((f) => ({
      ...f,
      colores: { ...f.colores, [modo]: { ...f.colores[modo], [key]: val } },
    }));
  };

  const guardar = async () => {
    if (!form.nombre.trim() && !edit?.es_default) {
      toast.error("Indica un nombre para la plantilla");
      return;
    }
    setGuardando(true);
    try {
      if (edit?.id) {
        const payload = { colores: form.colores };
        if (!edit.es_default) payload.nombre = form.nombre.trim();
        await api.put(`/plantillas-colores/${edit.id}`, payload);
        toast.success("Plantilla actualizada");
      } else {
        await api.post("/plantillas-colores", { nombre: form.nombre.trim(), colores: form.colores });
        toast.success("Plantilla creada");
      }
      setEdit(null);
      cargar();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setGuardando(false);
    }
  };

  const duplicar = async () => {
    if (!dupFor || !dupNombre.trim()) return;
    try {
      await api.post(`/plantillas-colores/${dupFor.id}/duplicar`, { nombre: dupNombre.trim() });
      toast.success("Plantilla duplicada");
      setDupFor(null);
      setDupNombre("");
      cargar();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const eliminar = async (p) => {
    if (!window.confirm(`¿Eliminar la plantilla «${p.nombre}»?`)) return;
    try {
      await api.delete(`/plantillas-colores/${p.id}`);
      toast.success("Plantilla eliminada");
      cargar();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const tituloDialog = useMemo(() => {
    if (!edit) return "";
    if (!edit.id) return "Nueva plantilla de colores";
    return edit.es_default ? "Plantilla por defecto" : `Editar — ${edit.nombre}`;
  }, [edit]);

  return (
    <div className="space-y-6" data-testid="plantillas-colores-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <PageHeader
          icon={Palette}
          title="Plantillas de colores"
          subtitle="Define la identidad visual por empresa (modo claro y oscuro)"
        />
        <Button onClick={abrirNueva} className="gap-1.5 shrink-0 bg-zinc-950 hover:bg-zinc-800">
          <Plus className="h-4 w-4" /> Nueva plantilla
        </Button>
      </div>

      {cargando ? (
        <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-indigo-500" /></div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {items.map((p) => (
            <article key={p.id} className="rounded-xl border border-zinc-200 bg-white p-4 shadow-sm dark:border-zinc-800 dark:bg-zinc-900">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <h3 className="font-semibold text-zinc-900 dark:text-zinc-100">{p.nombre}</h3>
                  <p className="mt-1 text-xs text-zinc-500">{p.num_empresas} empresa(s)</p>
                </div>
                {p.es_default && <Badge variant="secondary">Por defecto</Badge>}
              </div>
              <div className="mt-3 flex gap-1">
                {[p.colores?.light?.brand, p.colores?.light?.["header-from"], p.colores?.light?.primary].filter(Boolean).map((c, i) => (
                  <div key={i} className="h-8 flex-1 rounded-md border border-zinc-200" style={{ background: hslToCss(c) }} />
                ))}
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                <Button size="sm" variant="outline" className="gap-1" onClick={() => abrirEditar(p)}>
                  <Pencil className="h-3.5 w-3.5" /> Editar
                </Button>
                {!p.es_default && (
                  <>
                    <Button size="sm" variant="outline" className="gap-1" onClick={() => { setDupFor(p); setDupNombre(`${p.nombre} (copia)`); }}>
                      <Copy className="h-3.5 w-3.5" /> Duplicar
                    </Button>
                    <Button size="sm" variant="outline" className="gap-1 text-rose-600" onClick={() => eliminar(p)}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </>
                )}
              </div>
            </article>
          ))}
        </div>
      )}

      <Dialog open={!!edit} onOpenChange={(v) => !v && setEdit(null)}>
        <DialogContent className="max-h-[92vh] max-w-4xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">{tituloDialog}</DialogTitle>
          </DialogHeader>
          {!edit?.es_default && (
            <div className="space-y-1.5">
              <Label>Nombre</Label>
              <Input value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} disabled={!!edit?.id && edit?.es_default} />
            </div>
          )}
          <Tabs value={modoPreview} onValueChange={setModoPreview}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="light">Modo claro</TabsTrigger>
              <TabsTrigger value="dark">Modo oscuro</TabsTrigger>
            </TabsList>
            <TabsContent value="light" className="mt-4 space-y-4">
              <PreviewPanel colores={form.colores} modo="light" />
              {TOKEN_GROUPS.map((g) => (
                <TokenGroup
                  key={g.id}
                  group={g}
                  values={form.colores?.light}
                  onChange={(k, v) => onTokenChange("light", k, v)}
                />
              ))}
            </TabsContent>
            <TabsContent value="dark" className="mt-4 space-y-4">
              <PreviewPanel colores={form.colores} modo="dark" />
              {TOKEN_GROUPS.map((g) => (
                <TokenGroup
                  key={g.id}
                  group={g}
                  values={form.colores?.dark}
                  onChange={(k, v) => onTokenChange("dark", k, v)}
                />
              ))}
            </TabsContent>
          </Tabs>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEdit(null)}>Cancelar</Button>
            <Button onClick={guardar} disabled={guardando} className="bg-zinc-950 hover:bg-zinc-800">
              {guardando ? <Loader2 className="h-4 w-4 animate-spin" /> : "Guardar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!dupFor} onOpenChange={(v) => !v && setDupFor(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Duplicar plantilla</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-zinc-600">Se copiarán todos los colores de «{dupFor?.nombre}» a una plantilla nueva.</p>
          <div className="space-y-1.5">
            <Label>Nombre de la nueva plantilla</Label>
            <Input value={dupNombre} onChange={(e) => setDupNombre(e.target.value)} />
          </div>
          <DialogFooter>
            <Button onClick={duplicar} disabled={!dupNombre.trim()} className="bg-zinc-950 hover:bg-zinc-800">Crear copia</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
